import type { ReactNode } from 'react'
import { useBooleanFlagValue } from '@openfeature/react-sdk'

import { Alert, Text } from '@/ui'
import { FEATURE_FLAG_KEYS } from '@/flags'
import { TypographyStyles } from '@/styles'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useReselector } from '@/utils/sharedHooks'
import {
  selectActiveQTEDetailsAsDates,
  selectIsPastOrSameAsTaxEstimateIRSPaymentDate,
} from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.selector'

const PENALTY_SOURCE = {
  federal: 'an IRS late payment penalty',
  state: 'a state late payment penalty',
  both: 'IRS and state late payment penalties',
} as const

/**
 * Warns that the active quarter's IRS payment date has passed. `agencies` picks
 * which authority the penalty names, so a reader who owes only their state is
 * not told the IRS will penalize them.
 */
export const LatePaymentRiskCallout = ({
  agencies = 'federal',
  className,
  children,
}: {
  agencies?: keyof typeof PENALTY_SOURCE
  className?: string
  children?: ReactNode
}) => {
  const activeQuarter = useReselector(selectActiveQTEDetailsAsDates)
  const isPastPaymentDate = useReselector(
    selectIsPastOrSameAsTaxEstimateIRSPaymentDate
  )
  const alwaysOnQteEnabled = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.alwaysOnQteEnabled,
    false
  )

  // The stored date's calendar day re-zoned to the viewer, not the end-of-day
  // deadline in withOffset, which reads a day later for negative offsets.
  const dueDate = activeQuarter?.forDisplay.irsPaymentDue

  if (!alwaysOnQteEnabled || !isPastPaymentDate || !dueDate) {
    return null
  }

  return (
    <Alert variant="warning" className={className}>
      <Text variant={TypographyStyles.BODY_SM}>
        Payments made after{' '}
        <b>{dueDate.toFormat(DATE_FORMATS_LUXON.MONTH_DAY_LONG)}</b> may be
        subject to {PENALTY_SOURCE[agencies]}. We recommend paying as soon as
        possible to limit your risk. {children}
      </Text>
    </Alert>
  )
}
